import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Layout, Menu, Button, Dropdown, Space, Tooltip, message, Badge, Spin } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  SettingOutlined,
  TeamOutlined,
  SafetyOutlined,
  FileTextOutlined,
  LogoutOutlined,
  TrophyOutlined,
  DollarOutlined,
  TransactionOutlined,
  WalletOutlined,
  ShoppingOutlined,
  CreditCardOutlined,
  BankOutlined,
  LockOutlined,
  PayCircleOutlined,
  ReloadOutlined,
  MessageOutlined,
  CommentOutlined,
  BellOutlined,
  SendOutlined,
  WechatOutlined,
  ShopOutlined,
  LikeOutlined,
  CarOutlined,
  BoxPlotOutlined,
  CrownOutlined,
  FolderOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, fetchUserPermissions } from '../../store/slices/userSlice';
import { 
  fetchAdminNotificationList, 
  markNotificationAsRead, 
  markAllNotificationsAsRead 
} from '../../store/slices/notificationSlice';
import { MENU } from '../../constants/permissions';
import './style.css';
import { addTab, setActiveTab } from '../../store/slices/tabsSlice';
import TabsContainer from '../TabsContainer';
import customerServiceWebSocket from '../../utils/websocket';
import { MessageType } from '../../utils/websocket';
import { getAudioSettings, getAudioUrl } from '../../utils/audioSettings';


const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentUser, permissions } = useSelector(state => state.user);
  const { panes, activeKey } = useSelector(state => state.tabs);
  const { 
    notifications, 
    total: unreadCount, 
    loading: notificationLoading,
    current: currentPage,
    pageSize,
    totalPages
  } = useSelector(state => state.notification.adminNotifications);

  // 初始化通知列表（仅在用户登录后首次加载）
  useEffect(() => {
    if (currentUser) {
      dispatch(fetchAdminNotificationList({
        page: 1,
        pageSize: 10,
        isRead: false
      }));
    }
  }, [currentUser, dispatch]);

  // WebSocket 监听系统通知
  useEffect(() => {
    if (currentUser) {
      const handleSystemNotification = () => {
        // // 收到通知后重新获取通知列表
        dispatch(fetchAdminNotificationList({
          page: 1,
          pageSize: 10,
          isRead: false
        }));
      };

      customerServiceWebSocket.onMessage(MessageType.SYSTEM_NOTIFICATION, handleSystemNotification);

      return () => {
        customerServiceWebSocket.offMessage(MessageType.SYSTEM_NOTIFICATION, handleSystemNotification);
      };
    }
  }, [currentUser, dispatch]);

  // 标记单个通知为已读
  const handleMarkNotificationAsRead = (notificationId) => {
    dispatch(markNotificationAsRead(notificationId));
  };

  // 标记所有通知为已读
  const handleMarkAllNotificationsAsRead = () => {
    dispatch(markAllNotificationsAsRead());
  };

  // 加载更多通知
  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      dispatch(fetchAdminNotificationList({
        page: currentPage + 1,
        pageSize: 10,
        isRead: false
      }));
    }
  };

  // 通知类型到路由的映射
  const NOTIFICATION_TYPE_TO_ROUTE = {
    // 商家入驻申请
    MERCHANT_ENTRY_APPLICATION: '/product/shops',
    
    // 陪逛服务提供者申请
    ACCOMPANY_SHOPPING_PROVIDER_APPLICATION: '/service/accompany',
    
    // 商品审核
    GOODS_AUDIT: '/product/management',
    
    // 动态审核
    MOMENT_POST_AUDIT: '/purchase/and/dynamics',
    
    // 购买动态审核
    BUYING_POST_AUDIT: '/purchase/and/dynamics',
    
    // 投诉反馈
    COMPLAINT_FEEDBACK: '/communication/feedback',
    
    // 售后服务
    AFTER_SALE_SERVICE: '/order/after-sale',
    
    // 默认跳转到系统用户管理
    DEFAULT: '/system/users'
  };

  // 处理通知点击事件
  const handleNotificationClick = (notification) => {
    // 标记通知为已读
    dispatch(markNotificationAsRead(notification.id));

    // 根据通知类型跳转
    const route = NOTIFICATION_TYPE_TO_ROUTE[notification.type] || NOTIFICATION_TYPE_TO_ROUTE.DEFAULT;
    
    // 移除开头的 '/'
    const cleanRoute = route.startsWith('/') ? route.slice(1) : route;
    
    // 获取菜单项的标题
    const menuItem = allMenuItems.find(item => 
      item.key === cleanRoute || 
      (item.children && item.children.some(child => child.key === cleanRoute))
    );
    
    const menuTitle = menuItem 
      ? (menuItem.label || menuItem.title || cleanRoute) 
      : cleanRoute;

    // 添加标签页
    dispatch(addTab({ 
      key: cleanRoute, 
      title: menuTitle, 
      path: route 
    }));
    
    // 导航到对应路由
    navigate(route);
  };

  // 通知下拉菜单
  const notificationMenu = {
    // 阻止关闭下拉菜单
    onItemClick: (e) => {
      // 阻止事件冒泡，防止关闭下拉菜单
      e.domEvent.stopPropagation();
    },
    items: notifications.length === 0 
      ? [
          {
            key: 'empty_notification',
            label: (
              <div 
                style={{ 
                  textAlign: 'center', 
                  color: '#999', 
                  padding: '20px 0' 
                }}
              >
                暂无新通知
              </div>
            )
          }
        ]
      : [
          {
            key: 'clear_all',
            label: '全部标记为已读',
            onClick: handleMarkAllNotificationsAsRead
          },
          ...notifications.map((notification, index) => ({
            key: `notification_${index}`,
            label: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: notification.isRead ? '#f5f5f5' : '#fff', // 已读和未读背景色不同
                  padding: '8px 12px',
                  borderRadius: '4px',
                  transition: 'background-color 0.3s',
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <div style={{ flex: 1, marginRight: '12px' }}>
                  <div 
                    style={{ 
                      fontWeight: notification.isRead ? 'normal' : 'bold', 
                      color: notification.isRead ? '#999' : '#000' 
                    }}
                  >
                    {notification.title}
                  </div>
                  <div 
                    style={{ 
                      color: notification.isRead ? '#999' : '#666', 
                      fontSize: '12px', 
                      marginTop: '4px' 
                    }}
                  >
                    {notification.content}
                  </div>
                  <small 
                    style={{ 
                      color: notification.isRead ? '#bbb' : '#999', 
                      marginTop: '4px', 
                      display: 'block' 
                    }}
                  >
                    {new Date(notification.createdAt).toLocaleString()}
                  </small>
                </div>
                {!notification.isRead && (
                  <div 
                    style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      backgroundColor: '#1890ff', 
                      marginRight: '8px' 
                    }} 
                  />
                )}
                {!notification.isRead && (
                  <Button
                    size="small"
                    type="link"
                    style={{ color: '#1890ff' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkNotificationAsRead(notification.id);
                    }}
                  >
                    标记已读
                  </Button>
                )}
              </div>
            )
          })),
          // 加载更多按钮
          currentPage < totalPages && {
            key: 'load_more',
            label: (
              <div
                style={{
                  textAlign: 'center',
                  padding: '8px 0',
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation(); // 阻止事件冒泡，防止关闭下拉菜单
                  handleLoadMore();
                }}
              >
                {notificationLoading ? <Spin size="small" /> : '加载更多'}
              </div>
            )
          }
        ].filter(Boolean)  // 过滤掉 false 值
  };

  // 确保权限数据已加载
  useEffect(() => {
    if (permissions.length === 0) {
      // 如果权限为空，可能需要重新获取
      console.warn('权限列表为空，可能需要重新获取');
    }
  }, [dispatch, permissions.length]);

  // 请求浏览器通知权限
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('此浏览器不支持通知功能');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }, []);

  // 显示浏览器通知
  const showBrowserNotification = useCallback((title, options) => {
    if (!('Notification' in window)) {
      console.warn('此浏览器不支持通知功能');
      return;
    }

    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: options.body || '',
        icon: options.icon || '/favicon.ico',
        badge: options.badge || '/favicon.ico',
        tag: options.tag || 'system-notification',
        requireInteraction: options.requireInteraction || false,
        ...options
      });

      // 点击通知时聚焦到窗口
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // 自动关闭通知（可选，默认5秒后关闭）
      const autoClose = options.autoClose !== false;
      if (autoClose) {
        setTimeout(() => {
          notification.close();
        }, options.duration || 5000);
      }
    }
  }, []);

  // 获取中文语音
  const getChineseVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    // 优先选择中文语音
    const chineseVoice = voices.find(voice => 
      voice.lang.includes('zh-CN') || voice.lang === 'zh-CN'
    ) || voices.find(voice => 
      voice.lang.includes('zh') || voice.lang.includes('CN')
    );
    return chineseVoice || null;
  }, []);

  // 播放音效
  const playNotificationSound = useCallback(() => {
    const settings = getAudioSettings();
    
    // 如果音效被禁用，直接返回
    if (!settings.enabled) {
      return;
    }

    try {
      // 获取音效文件 URL
      const audioUrl = getAudioUrl();
      
      if (!audioUrl) {
        console.warn('未配置音效文件路径');
        return;
      }

      // 创建音频对象
      const audio = new Audio(audioUrl);
      
      // 设置音量
      audio.volume = settings.volume || 1.0;
      
      // 播放音效
      audio.play().catch(error => {
        console.error('播放音效失败:', error);
        // 如果自定义音效加载失败，尝试使用默认音效
        if (settings.useCustomAudio && settings.customAudioUrl) {
          const defaultAudio = new Audio(settings.defaultAudioUrl);
          defaultAudio.volume = settings.volume || 1.0;
          defaultAudio.play().catch(err => {
            console.error('播放默认音效也失败:', err);
          });
        }
      });

      // 清理：播放完成后移除音频对象（可选）
      audio.addEventListener('ended', () => {
        audio.remove();
      });

      // 错误处理
      audio.addEventListener('error', (e) => {
        console.error('音效加载错误:', e);
        // 如果自定义音效加载失败，尝试使用默认音效
        if (settings.useCustomAudio && settings.customAudioUrl && audioUrl !== settings.defaultAudioUrl) {
          const defaultAudio = new Audio(settings.defaultAudioUrl);
          defaultAudio.volume = settings.volume || 1.0;
          defaultAudio.play().catch(err => {
            console.error('播放默认音效也失败:', err);
          });
        }
      });

    } catch (error) {
      console.error('创建音效对象失败:', error);
    }
  }, []);

  // 语音播报
  const speakNotification = useCallback((text) => {
    if (!('speechSynthesis' in window)) {
      console.warn('此浏览器不支持语音合成功能');
      return;
    }

    // 停止当前正在播放的语音（如果有）
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    // 创建语音合成实例
    const utterance = new SpeechSynthesisUtterance(text);
    
    // 设置语音参数
    utterance.lang = 'zh-CN'; // 设置为中文
    utterance.rate = 1.0; // 语速 (0.1 到 10，默认 1)
    utterance.pitch = 1.0; // 音调 (0 到 2，默认 1)
    utterance.volume = 1.0; // 音量 (0 到 1，默认 1)

    // 尝试使用中文语音（如果可用）
    const chineseVoice = getChineseVoice();
    if (chineseVoice) {
      utterance.voice = chineseVoice;
    }

    // 播放语音
    window.speechSynthesis.speak(utterance);

    // 处理错误
    utterance.onerror = (event) => {
      console.error('语音播报错误:', event);
    };
  }, [getChineseVoice]);

  // 监听系统通知
  const handleSystemNotification = useCallback((data) => {
    console.log('收到系统通知:', data);
    const newNotification = {
      id: Date.now().toString(),
      content: data.data.content,
      type: data.messageType,
      timestamp: new Date(data.data.timestamp),
      isRead: false
    };

    dispatch(fetchAdminNotificationList({
      pageNum: 1,
      pageSize: 10,
      isRead: false
    }));

    // 使用浏览器 Notification API 显示通知
    showBrowserNotification('系统通知', {
      body: newNotification.content,
      icon: '/favicon.ico',
      tag: `notification-${newNotification.id}`,
      requireInteraction: false,
      autoClose: true,
      duration: 5000
    });

    // 播放音效
    playNotificationSound();

    // 语音播报通知内容
    speakNotification(newNotification.content);

    message.info(newNotification.content);
  }, [dispatch, showBrowserNotification, playNotificationSound, speakNotification]);

  // 初始化 WebSocket 通知监听
  useEffect(() => {
    if (currentUser) {
      // 请求通知权限
      requestNotificationPermission();

      let handleVoicesChanged = null;

      // 预加载语音合成引擎（某些浏览器需要）
      if ('speechSynthesis' in window) {
        // 某些浏览器需要先调用一次 getVoices 才能正确加载语音
        window.speechSynthesis.getVoices();
        
        // 监听语音列表加载完成事件（某些浏览器异步加载）
        handleVoicesChanged = () => {
          window.speechSynthesis.getVoices();
        };
        window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
        
        // 延迟加载语音列表（某些浏览器需要时间加载）
        setTimeout(() => {
          window.speechSynthesis.getVoices();
        }, 100);
      }

      // 监听系统通知消息
      customerServiceWebSocket.onMessage(MessageType.SYSTEM_NOTIFICATION, handleSystemNotification);

      // 清理函数
      return () => {
        customerServiceWebSocket.offMessage(MessageType.SYSTEM_NOTIFICATION, handleSystemNotification);
        
        // 清理语音合成相关
        if ('speechSynthesis' in window) {
          // 清理事件监听器
          if (handleVoicesChanged) {
            window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
          }
          // 停止所有语音播报
          if (window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
          }
        }
      };
    }
  }, [currentUser, handleSystemNotification, requestNotificationPermission]);

  // 检查是否有权限访问某个菜单
  const hasPermission = (permissionCode) => {
    return permissions.includes(permissionCode);
  };

  // 定义所有可能的菜单项（使用 useMemo 避免每次渲染都创建新数组）
  const allMenuItems = useMemo(() => [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
      title: '个人中心',
      // 个人中心为通用功能，不需要权限控制
    },
    {
      key: 'system',
      icon: <SettingOutlined />,
      label: '系统管理',
      title: '系统管理',
      permission: MENU.SYSTEM,
      children: [
        {
          key: 'system/users',
          icon: <UserOutlined />,
          label: '用户管理',
          title: '用户管理',
          permission: MENU.SYSTEM_USER,
        },

        {
          key: 'system/roles',
          icon: <TeamOutlined />,
          label: '角色管理',
          title: '角色管理',
          permission: MENU.SYSTEM_ROLE,
        },
        {
          key: 'system/permissions',
          icon: <SafetyOutlined />,
          label: '权限管理',
          title: '权限管理',
          permission: MENU.SYSTEM_PERMISSION,
        },
        {
          key: 'system/logs',
          icon: <FileTextOutlined />,
          label: '日志管理',
          title: '日志管理',
          permission: MENU.SYSTEM_LOG,
        },
        {
          key: 'system/operation-log',
          icon: <FileTextOutlined />,
          label: '系统操作日志',
          title: '系统操作日志',
          permission: MENU.SYSTEM_OPERATION_LOG,
        },
        {
          key: 'system/error-log',
          icon: <FileTextOutlined />,
          label: '系统错误日志',
          title: '系统错误日志',
          permission: MENU.SYSTEM_ERROR_LOG,
        },
        {
          key: 'system/settings',
          icon: <SettingOutlined />,
          label: '系统设置',
          title: '系统设置',
          permission: MENU.SYSTEM_SETTINGS,
        },
        {
          key: 'banner/management',
          icon: <FileTextOutlined />,
          label: '轮播图管理',
          title: '轮播图管理',
          permission: MENU.BANNER_MANAGEMENT,
        },
      ],
    },
   
     

        {
          key: 'reward/finance',
          icon: <DollarOutlined />,
          label: '财务管理',
          title: '财务管理',
          permission: MENU.REWARD_FINANCE,
          children: [
            {
              key: 'reward/finance/withdraw',
              icon: <WalletOutlined />,
              label: '提现管理',
              title: '提现管理',
              permission: MENU.REWARD_FINANCE_WITHDRAW,
            },
            {
              key: 'reward/finance/transaction',
              icon: <TransactionOutlined />,
              label: '交易记录',
              title: '交易记录',
              permission: MENU.REWARD_FINANCE_TRANSACTION,
            },
            {
              key: 'reward/finance/order',
              icon: <ShoppingOutlined />,
              label: '支付订单',
              title: '支付订单',
              permission: MENU.REWARD_FINANCE_ORDER,
            },
            // {
            //   key: 'reward/finance/precharge',
            //   icon: <CreditCardOutlined />,
            //   label: '预充值订单',
            //   title: '预充值订单',
            //   permission: MENU.REWARD_FINANCE_PRECHARGE,
            // },

            {
              key: 'reward/finance/freeze-record',
              icon: <LockOutlined />,
              label: '账户冻结记录',
              title: '账户冻结记录',
              permission: MENU.REWARD_FINANCE_FREEZE_RECORD,
            },
            {
              key: 'reward/finance/payment',
              icon: <PayCircleOutlined />,
              label: '支付接口集成',
              title: '支付接口集成',
              permission: MENU.REWARD_FINANCE_PAYMENT,
            },
            {
              key: 'reward/account',
              icon: <BankOutlined />,
              label: '账户管理',
              title: '账户管理',
              permission: MENU.REWARD_ACCOUNT,
            },
          ],
        },
        {
          key: 'reward/login-verification',
          icon: <LockOutlined />,
          label: '登录认证管理',
          title: '登录认证管理',
          permission: undefined, // 通过children决定是否显示
          children: [
            {
              key: 'reward/login',
              icon: <UserOutlined />,
              label: '登录管理',
              title: '登录管理',
              permission: MENU.REWARD_LOGIN,
            },
            {
              key: 'reward/verification/user',
              icon: <UserOutlined />,
              label: '用户认证',
              title: '用户认证',
              permission: MENU.REWARD_VERIFICATION_USER,
            },
          ],
        },
    {
      key: 'product-order',
      icon: <ShoppingOutlined />,
      label: '商品与订单管理',
      title: '商品与订单管理',
      permission: undefined, // 通过children决定是否显示
      children: [
        {
          key: 'product/shops',
          icon: <ShopOutlined />,
          label: '店铺管理',
          title: '店铺管理',
          permission: MENU.PRODUCT_SHOP,
        },
        {
          key: 'store/visit/statistics',
          icon: <ShopOutlined />,
          label: '店铺访问统计',
          title: '店铺访问统计',
          permission: MENU.STORE_VISIT_STATISTICS_MENU,
        },
        {
          key: 'product/management',
          icon: <BoxPlotOutlined />,
          label: '商品管理',
          title: '商品管理',
          permission: MENU.PRODUCT_MANAGEMENT,
        },
        {
          key: 'order',
          icon: <BoxPlotOutlined />,
          label: '订单中心',
          title: '订单中心',
          permission: MENU.ORDER_MANAGEMENT,
        },
        {
          key: 'order/shipping',
          icon: <CarOutlined />,
          label: '发货中心',
          title: '发货中心',
          permission: MENU.ORDER_SHIPPING,
        },
        {
          key: 'order/quality',
          icon: <SafetyOutlined />,
          label: '质检中心',
          title: '质检中心',
          permission: MENU.ORDER_QUALITY,
        },
        {
          key: 'order/after-sale',
          icon: <SafetyOutlined />,
          label: '售后服务',
          title: '售后服务',
          permission: MENU.ORDER_QUALITY,
        },
        {
          key: 'logistics',
          icon: <CarOutlined />,
          label: '物流管理',
          title: '物流管理',
          permission: MENU.LOGISTICS_MANAGEMENT,
        },
        {
          key: 'shopping/cart',
          icon: <ShoppingOutlined />,
          label: '购物车管理',
          title: '购物车管理',
          permission: MENU.SHOPPING_CART,
        },
        {
      key: 'user/address',
      icon: <UserOutlined />,
      label: '地址管理',
      title: '地址管理',
      permission: MENU.USER_ADDRESS,
    },
  ],
},
{
  key: 'purchase-service',
      icon: <FileTextOutlined />,
      label: '求购与服务管理',
      title: '求购与服务管理',
      permission: undefined, // 通过children决定是否显示
      children: [
        {
          key: 'purchase/and/dynamics',
          icon: <FileTextOutlined />,
          label: '求购与动态',
          title: '求购与动态',
          permission: MENU.PURCHASE_AND_DYNAMICS,
        },
        {
          key: 'service/custom',
          icon: <FileTextOutlined />,
          label: '定制服务',
          title: '定制服务',
          permission: MENU.SERVICE_CUSTOM,
        },
        {
          key: 'service/accompany',
          icon: <FileTextOutlined />,
          label: '陪逛服务',
          title: '陪逛服务',
          permission: MENU.SERVICE_ACCOMPANY,
        },
        {
          key: 'service/platform-sales',
          icon: <ShoppingOutlined />,
          label: '平台代售',
          title: '平台代售',
          permission: MENU.SERVICE_PLATFORM_SALES,
        },
      ],
    },
    {
      key: 'content',
      icon: <FileTextOutlined />,
      label: '内容管理',
      title: '内容管理',
      permission: undefined, // 通过children决定是否显示
      children: [
        {
          key: 'help/center',
          icon: <FileTextOutlined />,
          label: '帮助中心',
          title: '帮助中心',
          permission: MENU.HELP_CENTER,
        },
        {
          key: 'article/management/list',
          icon: <FileTextOutlined />,
          label: '资讯文章',
          title: '资讯文章',
          permission: MENU.ARTICLE_MANAGEMENT,
        },
        {
          key: 'article/type/management',
          icon: <FileTextOutlined />,
          label: '文章类型',
          title: '文章类型',
          permission: MENU.ARTICLE_TYPE_MANAGEMENT,
        },
      ],
    },
    {
      key: 'jade-service',
      icon: <CrownOutlined />,
      label: '玉石服务管理',
      title: '玉石服务管理',
      permission: undefined,
      children: [
        {
          key: 'jade-identification-service',
          icon: <CrownOutlined />,
          label: '玉石鉴定服务',
          title: '玉石鉴定服务',
          permission: MENU.JADE_IDENTIFICATION_SERVICE,
        },
        {
          key: 'jade-identification-article/management',
          icon: <FileTextOutlined />,
          label: '玉石鉴定知识文章',
          title: '玉石鉴定知识文章',
          permission: MENU.JADE_IDENTIFICATION_ARTICLE_MANAGEMENT,
        },
        {
          key: 'jade-identification-category/management',
          icon: <FolderOutlined />,
          label: '玉石鉴定分类',
          title: '玉石鉴定分类',
          permission: MENU.JADE_IDENTIFICATION_CATEGORY,
        },
        {
          key: 'jade-identification-order/management',
          icon: <FileTextOutlined />,
          label: '玉石鉴定订单',
          title: '玉石鉴定订单',
          permission: MENU.JADE_IDENTIFICATION_ORDER,
        },
        {
          key: 'article/redeem-code',
          icon: <FileTextOutlined />,
          label: '兑换码管理',
          title: '兑换码管理',
          permission: MENU.ARTICLE_REDEEM_CODE,
        },
        {
          key: 'user-article-redeem',
          icon: <FileTextOutlined />,
          label: '用户兑换管理',
          title: '用户付费文章服务兑换管理',
          permission: MENU.USER_ARTICLE_REDEEM,
        },
      ],
    },
    {
      key: 'interaction-communication',
      icon: <MessageOutlined />,
      label: '互动与通信管理',
      title: '互动与通信管理',
      permission: undefined, // 通过children决定是否显示
      children: [
        {
          key: 'interaction/message',
          icon: <MessageOutlined />,
          label: '互动消息管理',
          title: '互动消息管理',
          permission: MENU.INTERACTION_MESSAGE,
        },
        {
          key: 'like/management',
          icon: <LikeOutlined />,
          label: '点赞管理',
          title: '点赞管理',
          permission: MENU.LIKE_MANAGEMENT,
        },
        {
          key: 'communication/notifications',
          icon: <BellOutlined />,
          label: '系统通知管理',
          title: '系统通知管理',
          permission: MENU.COMMUNICATION_NOTIFICATION,
        },
        {
          key: 'communication/chat',
          icon: <WechatOutlined />,
          label: '聊天消息管理',
          title: '聊天消息管理',
          permission: MENU.COMMUNICATION_CHAT,
        },
        // {
        //   key: 'communication/push',
        //   icon: <SendOutlined />,
        //   label: '消息推送管理',
        //   title: '消息推送管理',
        //   permission: MENU.COMMUNICATION_PUSH,
        // },
        {
          key: 'communication/feedback',
          icon: <CommentOutlined />,
          label: '反馈管理',
          title: '反馈管理',
          permission: MENU.COMMUNICATION_FEEDBACK,
        },
      ],
    }
     
  ], []);

  // 递归过滤菜单项
  const filterMenuItems = (items) => {
    return items.filter(item => {
      const hasPermissionOrNoNeed = !item.permission || hasPermission(item.permission);
      
      
      if (item.children) {
        const filteredChildren = filterMenuItems(item.children);
        item.children = filteredChildren;
        const shouldKeep = hasPermissionOrNoNeed && filteredChildren.length > 0;
        
        return shouldKeep;
      }
      
      return hasPermissionOrNoNeed;
    });
  };

  // 获取经过权限过滤的菜单项
  const menuItems = filterMenuItems(allMenuItems);

  // 递归查找菜单项，根据路径找到对应的菜单项
  const findMenuItemByPath = useCallback((path, items) => {
    for (const item of items) {
      // 检查当前项是否匹配
      if (item.key && path === `/${item.key}`) {
        return item;
      }
      // 递归检查子项
      if (item.children && item.children.length > 0) {
        const found = findMenuItemByPath(path, item.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, []);

  // 递归查找菜单项，根据key找到对应的菜单项及其父级路径
  const findMenuItemByKey = useCallback((key, items, parentPath = []) => {
    for (const item of items) {
      const currentPath = [...parentPath, item];
      
      // 检查当前项是否匹配
      if (item.key === key) {
        return { item, path: currentPath };
      }
      
      // 递归检查子项
      if (item.children && item.children.length > 0) {
        const found = findMenuItemByKey(key, item.children, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, []);

  // 根据当前活跃的tab或路由计算菜单的selectedKeys和openKeys
  const getMenuSelectedKeys = useMemo(() => {
    // 优先使用activeKey，如果没有则使用location.pathname
    const currentKey = activeKey || location.pathname.replace('/', '');
    
    if (!currentKey || currentKey === 'dashboard' || currentKey === 'login' || currentKey === '403') {
      return { selectedKeys: [], openKeys: [] };
    }

    // 在allMenuItems中查找对应的菜单项
    const result = findMenuItemByKey(currentKey, allMenuItems);
    
    if (result && result.item) {
      const selectedKeys = [result.item.key];
      // 获取所有父级菜单的key作为openKeys
      const openKeys = result.path
        .slice(0, -1) // 排除当前项本身
        .map(node => node.key)
        .filter(Boolean);
      
      return { selectedKeys, openKeys };
    }

    // 如果通过key找不到，尝试通过pathname查找
    const menuItem = findMenuItemByPath(location.pathname, allMenuItems);
    if (menuItem) {
      const result = findMenuItemByKey(menuItem.key, allMenuItems);
      if (result && result.item) {
        const selectedKeys = [result.item.key];
        const openKeys = result.path
          .slice(0, -1)
          .map(node => node.key)
          .filter(Boolean);
        return { selectedKeys, openKeys };
      }
    }

    return { selectedKeys: [], openKeys: [] };
  }, [activeKey, location.pathname, findMenuItemByKey, findMenuItemByPath, allMenuItems]);

  // 获取菜单的选中和展开状态
  const { selectedKeys, openKeys } = getMenuSelectedKeys;
  
  // 菜单展开状态
  const [menuOpenKeys, setMenuOpenKeys] = useState(openKeys);
  
  // 使用 ref 存储上一次的 openKeys，用于比较
  const prevOpenKeysRef = useRef([]);
  
  // 当openKeys变化时，更新menuOpenKeys（合并而不是替换，保留用户手动展开的菜单）
  useEffect(() => {
    // 比较 openKeys 是否真的改变了
    const currentOpenKeysStr = JSON.stringify([...openKeys].sort());
    const prevOpenKeysStr = JSON.stringify([...prevOpenKeysRef.current].sort());
    
    // 只有当 openKeys 真正改变时才更新
    if (currentOpenKeysStr !== prevOpenKeysStr && openKeys.length > 0) {
      // 合并新的openKeys和现有的menuOpenKeys，确保父级菜单展开
      setMenuOpenKeys(prevKeys => {
        const mergedKeys = [...new Set([...prevKeys, ...openKeys])];
        // 只有当合并后的keys与当前keys不同时才更新
        const prevKeysStr = JSON.stringify([...prevKeys].sort());
        const mergedKeysStr = JSON.stringify([...mergedKeys].sort());
        if (prevKeysStr !== mergedKeysStr) {
          return mergedKeys;
        }
        return prevKeys;
      });
      // 更新 ref
      prevOpenKeysRef.current = openKeys;
    }
  }, [openKeys]);

  // 监听路由变化，同步 tabs
  useEffect(() => {
    // 排除登录页和403页面
    if (location.pathname === '/login' || location.pathname === '/403' || location.pathname === '/dashboard') {
      return;
    }

    const currentPath = location.pathname;
    const menuItem = findMenuItemByPath(currentPath, allMenuItems);
    
    if (menuItem && menuItem.key) {
      const tabKey = menuItem.key;
      const tabTitle = menuItem.title || menuItem.label || tabKey;
      
      // 检查该 tab 是否已存在
      const existingTab = panes.find(pane => pane.key === tabKey);
      
      if (!existingTab) {
        // 如果不存在，添加新的 tab
        dispatch(addTab({
          key: tabKey,
          title: tabTitle,
          path: currentPath
        }));
      } else {
        // 如果存在，设置为活跃 tab
        if (activeKey !== tabKey) {
          dispatch(setActiveTab(tabKey));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, findMenuItemByPath, dispatch]);

  const handleMenuClick = ({ key, item }) => {
    // 从 item 的 props 中获取 label
    const menuTitle = item.props.title || item.props.label || key;
    
    // 添加标签页
    dispatch(addTab({ 
      key: key, 
      title: menuTitle, 
      path: `/${key}` 
    }));
    
    // 导航到对应路由
    navigate(`/${key}`);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // 刷新权限
  const handleRefreshPermissions = async () => {
    try {
      setRefreshing(true);
      await dispatch(fetchUserPermissions(true)).unwrap();
      message.success('权限刷新成功');
    } catch (error) {
      message.error('权限刷新失败');
    } finally {
      setRefreshing(false);
    }
  };

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        width={200}
        style={{ 
          overflow: 'auto', 
          height: '100vh', 
          position: 'fixed', 
          left: 0,
          zIndex: 10
        }}
        className="scrollbar-hidden" // 添加隐藏滚动条类名
      >
        <div className="logo">玉上石佛寺后台系统</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={menuOpenKeys}
          onOpenChange={setMenuOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ 
            height: 'calc(100vh - 64px)', 
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        />
      </Sider>
      <Layout 
        style={{ 
          marginLeft: collapsed ? 80 : 200,
          transition: 'margin-left 0.2s'
        }}
      >
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'fixed',
            width: collapsed ? 'calc(100% - 80px)' : 'calc(100% - 200px)',
            zIndex: 9,
            top: 0,
            right: 0,
            transition: 'width 0.2s'
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="刷新权限">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={handleRefreshPermissions}
                loading={refreshing}
                style={{ marginRight: 16 }}
              >
                刷新权限
              </Button>
            </Tooltip>

            {/* 通知图标 */}
            <Dropdown 
              menu={notificationMenu} 
              placement="bottomRight"
              trigger={['click']}
              dropdownRender={(menu) => (
                <div 
                  style={{
                    maxHeight: '400px', // 最大高度
                    overflowY: 'auto',  // 垂直滚动条
                    boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    width: '320px'  // 固定宽度
                  }}
                >
                  {menu}
                </div>
              )}
              style={{ marginRight: 16 }}
            >
              <Badge count={unreadCount} overflowCount={99}>
                <BellOutlined 
                  style={{ 
                    fontSize: '18px', 
                    cursor: 'pointer',
                    color: unreadCount > 0 ? '#1890ff' : 'inherit'
                  }} 
                />
              </Badge>
            </Dropdown>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space className="user-dropdown">
                <UserOutlined />
                <span>{currentUser?.username || '管理员'}</span>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '64px 16px 24px',
            padding: 0,
            minHeight: 280,
            background: 'transparent',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
          className="scrollbar-hidden" // 添加隐藏滚动条类名
        >
          <TabsContainer />
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;